import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

function memberLabel(m) {
  if (!m) return '—';
  const name = [m.first_name, m.last_name].filter(Boolean).join(' ');
  return `${name || '—'} (${m.affiliate_code || '—'})`;
}

function currentPeriod() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function StatusBadge({ status, isZh }) {
  const styles = {
    released: { backgroundColor: 'rgba(201,169,97,0.2)', color: '#c9a961', label: isZh ? '已发放' : 'Released' },
    pending: { backgroundColor: 'rgba(201,169,97,0.05)', color: '#b8a68f', label: isZh ? '待发放' : 'Pending' },
    flagged: { backgroundColor: 'rgba(217,119,6,0.15)', color: '#d97706', label: isZh ? '⚠ 已标记 — 已达上限' : '⚠ Flagged — cap reached' },
  };
  const s = styles[status] || styles.pending;
  return (
    <span className="px-2 py-1 rounded text-body-xs font-medium" style={{ backgroundColor: s.backgroundColor, color: s.color }}>
      {s.label}
    </span>
  );
}

function NumberEditor({ label, value, onSave, saving, suffix, min = 0, max = 100, allowClear = false, isZh }) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState('');

  const startEdit = () => {
    setEditValue(value === null || value === undefined ? '' : String(value));
    setEditing(true);
  };

  const save = async () => {
    if (allowClear && editValue.trim() === '') {
      await onSave(null);
      setEditing(false);
      return;
    }
    const parsed = parseFloat(editValue);
    if (isNaN(parsed) || parsed < min || (max !== null && parsed > max)) {
      alert(isZh
        ? `请输入一个${max !== null ? `${min} 到 ${max} 之间的` : `不小于 ${min} 的`}数字${allowClear ? '，或留空以清除' : ''}`
        : `Please enter a number${max !== null ? ` between ${min} and ${max}` : ` >= ${min}`}${allowClear ? ', or leave blank to clear' : ''}`);
      return;
    }
    await onSave(parsed);
    setEditing(false);
  };

  return (
    <div className="flex items-center justify-between p-3 border rounded" style={{ backgroundColor: '#152214', borderColor: '#3d5a4a' }}>
      <span className="text-body-sm" style={{ color: '#b8a68f' }}>{label}</span>
      {editing ? (
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.01"
            min={min}
            max={max ?? undefined}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            disabled={saving}
            placeholder={allowClear ? (isZh ? '留空 = 无' : 'blank = none') : ''}
            className="w-24 px-2 py-1 text-sm rounded border"
            style={{ borderColor: '#3d5a4a' }}
          />
          <button
            onClick={save}
            disabled={saving}
            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {saving ? (isZh ? '保存中...' : 'Saving...') : (isZh ? '保存' : 'Save')}
          </button>
          <button
            onClick={() => setEditing(false)}
            disabled={saving}
            className="px-3 py-1 text-xs rounded font-medium disabled:opacity-50"
            style={{ backgroundColor: '#3d5a4a', color: '#b8a68f' }}
          >
            {isZh ? '取消' : 'Cancel'}
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-3">
          <span className="font-medium" style={{ color: '#c9a961' }}>{value === null || value === undefined ? '—' : value}{value !== null && value !== undefined ? (suffix !== undefined ? suffix : '%') : ''}</span>
          <button
            onClick={startEdit}
            className="px-3 py-1 text-xs rounded font-medium"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {isZh ? '编辑' : 'Edit'}
          </button>
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-4 py-2 text-body-sm font-medium rounded-t-lg transition-colors"
      style={
        active
          ? { color: '#0d2818', backgroundColor: '#c9a961' }
          : { color: '#c9a961', backgroundColor: 'transparent' }
      }
    >
      {children}
    </button>
  );
}

export default function Monthly() {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const tt = (en, zh) => (isZh ? zh : en);

  const [activeTab, setActiveTab] = useState('team');
  const [period, setPeriod] = useState(currentPeriod());
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [recalculatingAll, setRecalculatingAll] = useState(false);

  // Team Incentives (star-rank cascading) state
  const [run, setRun] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState({});
  const [payoutRates, setPayoutRates] = useState([]);
  const [starRates, setStarRates] = useState([]);
  const [savingRateKey, setSavingRateKey] = useState(null);
  const [teamStatusTab, setTeamStatusTab] = useState('pending');
  const [teamExclusions, setTeamExclusions] = useState([]);
  const [teamExclusionsLoading, setTeamExclusionsLoading] = useState(true);
  const [teamExcludingId, setTeamExcludingId] = useState(null);

  // Company Pool (allocation-weighted) state
  const [companyRun, setCompanyRun] = useState(null);
  const [companyRecords, setCompanyRecords] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(true);
  const [companySelectedIds, setCompanySelectedIds] = useState({});
  const [companyPayoutRates, setCompanyPayoutRates] = useState([]);
  const [companyPackages, setCompanyPackages] = useState([]);
  const [savingPackageKey, setSavingPackageKey] = useState(null);
  const [companyStatusTab, setCompanyStatusTab] = useState('pending');
  const [companyExclusions, setCompanyExclusions] = useState([]);
  const [companyExclusionsLoading, setCompanyExclusionsLoading] = useState(true);
  const [companyExcludingId, setCompanyExcludingId] = useState(null);
  const [cumulativeByOwner, setCumulativeByOwner] = useState([]);
  const [cumulativeByOwnerLoading, setCumulativeByOwnerLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'admin' && user.role !== 'superadmin') {
      navigate('/dashboard');
    }
  }, [authLoading, user, navigate]);

  const isBjoechia = (user?.wellis_id || '').toUpperCase() === 'WELLIS-BJOECHIA';

  const fetchList = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/list?year=${period.year}&month=${period.month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Passive rewards');
      }
      setRun(data.run);
      setRecords(data.records || []);
      setSelectedIds({});
    } catch (err) {
      setError(err.message || tt('Failed to load Passive rewards', '加载被动奖励失败'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load rates');
      }
      setPayoutRates(data.payout_rates || []);
      setStarRates(data.star_rates || []);
    } catch (err) {
      setError(err.message || 'Failed to load rates');
    }
  };

  const fetchCompanyList = async () => {
    setCompanyLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/company-pool/list?year=${period.year}&month=${period.month}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load Passive');
      }
      setCompanyRun(data.run);
      setCompanyRecords(data.records || []);
      setCompanySelectedIds({});
    } catch (err) {
      setError(err.message || 'Failed to load Passive');
    } finally {
      setCompanyLoading(false);
    }
  };

  const fetchCompanyRates = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/rates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load rates');
      }
      setCompanyPayoutRates(data.payout_rates || []);
      setCompanyPackages(data.packages || []);
    } catch (err) {
      setError(err.message || 'Failed to load rates');
    }
  };

  const fetchTeamExclusions = async () => {
    setTeamExclusionsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/exclusions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load exclusions');
      }
      setTeamExclusions(data.exclusions || []);
    } catch (err) {
      setError(err.message || 'Failed to load exclusions');
    } finally {
      setTeamExclusionsLoading(false);
    }
  };

  const fetchCompanyExclusions = async () => {
    setCompanyExclusionsLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/exclusions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load exclusions');
      }
      setCompanyExclusions(data.exclusions || []);
    } catch (err) {
      setError(err.message || 'Failed to load exclusions');
    } finally {
      setCompanyExclusionsLoading(false);
    }
  };

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    fetchList();
    fetchCompanyList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token, period.year, period.month]);

  const fetchCumulativeByOwner = async () => {
    setCumulativeByOwnerLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/cumulative-by-owner`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Failed to load cumulative points by owner');
      }
      setCumulativeByOwner(data.owners || []);
    } catch (err) {
      setError(err.message || 'Failed to load cumulative points by owner');
    } finally {
      setCumulativeByOwnerLoading(false);
    }
  };

  useEffect(() => {
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) return;
    fetchRates();
    fetchCompanyRates();
    fetchTeamExclusions();
    fetchCompanyExclusions();
    fetchCumulativeByOwner();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token]);

  const handleCalculate = async () => {
    setCalculating(true);
    setError('');
    setStatusMessage('');
    const isTeam = activeTab === 'team';
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/${isTeam ? 'monthly-passive' : 'company-pool'}/calculate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ period_year: period.year, period_month: period.month }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to calculate');
      }
      setStatusMessage(tt(`Pool: ${Number(data.pool_total).toFixed(2)} CP · ${data.records_created} new record${data.records_created === 1 ? '' : 's'} created.`, `资金池：${Number(data.pool_total).toFixed(2)} CP · 新增 ${data.records_created} 条记录。`));
      if (isTeam) {
        await fetchList();
      } else {
        await fetchCompanyList();
      }
    } catch (err) {
      setError(err.message || 'Failed to calculate');
    } finally {
      setCalculating(false);
    }
  };

  const handleRelease = async (ids) => {
    if (!ids || ids.length === 0) return;
    setReleasing(true);
    setError('');
    setStatusMessage('');
    const isTeam = activeTab === 'team';
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/admin/${isTeam ? 'monthly-passive' : 'company-pool'}/release`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ record_ids: ids }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to release');
      }
      const failures = (data.results || []).filter((r) => !r.success);
      const successes = (data.results || []).filter((r) => r.success);
      if (failures.length > 0) {
        setStatusMessage(isZh
          ? `已发放 ${successes.length} 条，${failures.length} 条失败：${failures.map((f) => f.message).join('; ')}`
          : `Released ${successes.length}, ${failures.length} failed: ${failures.map((f) => f.message).join('; ')}`);
      } else {
        setStatusMessage(tt(`Released ${successes.length} record${successes.length === 1 ? '' : 's'}.`, `已发放 ${successes.length} 条记录。`));
      }
      if (isTeam) {
        await fetchList();
      } else {
        await fetchCompanyList();
      }
    } catch (err) {
      setError(err.message || 'Failed to release');
    } finally {
      setReleasing(false);
    }
  };

  const handleRecalculateAll = async () => {
    setRecalculatingAll(true);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/recalculate-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to recalculate purchases');
      }
      setStatusMessage(data.message);
      await fetchList();
    } catch (err) {
      setError(err.message || 'Failed to recalculate purchases');
    } finally {
      setRecalculatingAll(false);
    }
  };

  const handleExcludeTeam = async (recordId) => {
    const reason = window.prompt(tt('Reason for excluding this purchase from future Team Booster calculations (optional):', '将此笔购买从未来的团队激励计算中排除的原因（可选）：'), '');
    if (reason === null) return; // cancelled
    setTeamExcludingId(recordId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/exclude`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ record_id: recordId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to exclude purchase');
      }
      setStatusMessage(data.message);
      await fetchList();
      await fetchTeamExclusions();
    } catch (err) {
      setError(err.message || 'Failed to exclude purchase');
    } finally {
      setTeamExcludingId(null);
    }
  };

  const handleRemoveTeamExclusion = async (exclusionId) => {
    setTeamExcludingId(exclusionId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/remove-exclusion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ exclusion_id: exclusionId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove exclusion');
      }
      setStatusMessage(data.message);
      await fetchTeamExclusions();
    } catch (err) {
      setError(err.message || 'Failed to remove exclusion');
    } finally {
      setTeamExcludingId(null);
    }
  };

  const handleExcludeCompany = async (recordId) => {
    const reason = window.prompt(tt('Reason for excluding this purchase from future Passive calculations (optional):', '将此笔购买从未来的被动计算中排除的原因（可选）：'), '');
    if (reason === null) return; // cancelled
    setCompanyExcludingId(recordId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/exclude`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ record_id: recordId, reason: reason || null }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to exclude purchase');
      }
      setStatusMessage(data.message);
      await fetchCompanyList();
      await fetchCompanyExclusions();
    } catch (err) {
      setError(err.message || 'Failed to exclude purchase');
    } finally {
      setCompanyExcludingId(null);
    }
  };

  const handleRemoveCompanyExclusion = async (exclusionId) => {
    setCompanyExcludingId(exclusionId);
    setError('');
    setStatusMessage('');
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/remove-exclusion`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ exclusion_id: exclusionId }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to remove exclusion');
      }
      setStatusMessage(data.message);
      await fetchCompanyExclusions();
    } catch (err) {
      setError(err.message || 'Failed to remove exclusion');
    } finally {
      setCompanyExcludingId(null);
    }
  };

  const handleSavePayoutRate = async (rateKey, value) => {
    setSavingRateKey(rateKey);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/update-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rate_key: rateKey, rate_percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update rate');
      }
      await fetchRates();
    } catch (err) {
      setError(err.message || 'Failed to update rate');
    } finally {
      setSavingRateKey(null);
    }
  };

  const handleSaveStarRate = async (star, value) => {
    setSavingRateKey(`star-${star}`);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/monthly-passive/update-star-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ star, percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update star rate');
      }
      await fetchRates();
    } catch (err) {
      setError(err.message || 'Failed to update star rate');
    } finally {
      setSavingRateKey(null);
    }
  };

  const handleSaveCompanyRate = async (rateKey, value) => {
    setSavingPackageKey(rateKey);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/update-rate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ rate_key: rateKey, rate_percent: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update rate');
      }
      await fetchCompanyRates();
    } catch (err) {
      setError(err.message || 'Failed to update rate');
    } finally {
      setSavingPackageKey(null);
    }
  };

  const handleSaveWeight = async (packageCode, value) => {
    setSavingPackageKey(`weight-${packageCode}`);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/update-weight`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ package_code: packageCode, weight: value }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update weight');
      }
      await fetchCompanyRates();
    } catch (err) {
      setError(err.message || 'Failed to update weight');
    } finally {
      setSavingPackageKey(null);
    }
  };

  const handleSaveMaxPassiveCash = async (packageCode, value) => {
    setSavingPackageKey(`max-cash-${packageCode}`);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/update-passive-caps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_code: packageCode,
          max_passive_cash: value,
          max_passive_credit: companyPackages.find((p) => p.code === packageCode)?.max_passive_payout_credit ?? null,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update cap');
      }
      await fetchCompanyRates();
    } catch (err) {
      setError(err.message || 'Failed to update cap');
    } finally {
      setSavingPackageKey(null);
    }
  };

  const handleSaveMaxPassiveCredit = async (packageCode, value) => {
    setSavingPackageKey(`max-credit-${packageCode}`);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/company-pool/update-passive-caps`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          package_code: packageCode,
          max_passive_cash: companyPackages.find((p) => p.code === packageCode)?.max_passive_payout_cash ?? null,
          max_passive_credit: value,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update cap');
      }
      await fetchCompanyRates();
    } catch (err) {
      setError(err.message || 'Failed to update cap');
    } finally {
      setSavingPackageKey(null);
    }
  };

  const pendingRecords = useMemo(() => records.filter((r) => r.status === 'pending'), [records]);
  const releasedRecords = useMemo(() => records.filter((r) => r.status === 'released'), [records]);
  const flaggedRecords = useMemo(() => records.filter((r) => r.status === 'flagged'), [records]);
  const bonusCollecting = useMemo(() => pendingRecords.reduce((sum, r) => sum + Number(r.amount), 0), [pendingRecords]);
  const teamTabRecords = teamStatusTab === 'pending' ? pendingRecords
    : teamStatusTab === 'released' ? releasedRecords
    : flaggedRecords;
  const allPendingSelected = pendingRecords.length > 0 && pendingRecords.every((r) => selectedIds[r.id]);

  const toggleSelectAll = () => {
    if (allPendingSelected) {
      setSelectedIds({});
    } else {
      const next = {};
      pendingRecords.forEach((r) => { next[r.id] = true; });
      setSelectedIds(next);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const selectedCount = Object.values(selectedIds).filter(Boolean).length;

  const companyPendingRecords = useMemo(() => companyRecords.filter((r) => r.status === 'pending'), [companyRecords]);
  const companyReleasedRecords = useMemo(() => companyRecords.filter((r) => r.status === 'released'), [companyRecords]);
  const companyFlaggedRecords = useMemo(() => companyRecords.filter((r) => r.status === 'flagged'), [companyRecords]);
  const companyTabRecords = companyStatusTab === 'pending' ? companyPendingRecords
    : companyStatusTab === 'released' ? companyReleasedRecords
    : companyFlaggedRecords;
  const companyAllPendingSelected = companyPendingRecords.length > 0 && companyPendingRecords.every((r) => companySelectedIds[r.id]);

  const toggleCompanySelectAll = () => {
    if (companyAllPendingSelected) {
      setCompanySelectedIds({});
    } else {
      const next = {};
      companyPendingRecords.forEach((r) => { next[r.id] = true; });
      setCompanySelectedIds(next);
    }
  };

  const toggleCompanySelect = (id) => {
    setCompanySelectedIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const companySelectedCount = Object.values(companySelectedIds).filter(Boolean).length;

  if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
    return null;
  }

  const passiveMonthlyRate = payoutRates.find((r) => r.rate_key === 'PASSIVE_MONTHLY');
  const splitCashRate = payoutRates.find((r) => r.rate_key === 'PASSIVE_MONTHLY_SPLIT_CASH_PERCENT');
  const splitCashPercent = splitCashRate ? Number(splitCashRate.rate_percent) : 50;

  const companyRate = companyPayoutRates.find((r) => r.rate_key === 'COMPANY_POOL_RATE');
  const companySplitRate = companyPayoutRates.find((r) => r.rate_key === 'COMPANY_POOL_SPLIT_CASH_PERCENT');
  const companySplitPercent = companySplitRate ? Number(companySplitRate.rate_percent) : 50;

  return (
    <div className="min-h-screen px-4 pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
    <div className="max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-headline-lg font-bold" style={{ color: '#c9a961' }}>
          {tt('Passive', '被动')}
        </h1>
        {activeTab !== 'rates' && (
        <div className="flex items-center gap-3">
          <input
            type="month"
            value={`${period.year}-${String(period.month).padStart(2, '0')}`}
            onChange={(e) => {
              const [y, m] = e.target.value.split('-').map((v) => parseInt(v, 10));
              if (y && m) setPeriod({ year: y, month: m });
            }}
            className="px-3 py-2 rounded-lg border text-body-sm"
            style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#c9a961' }}
          />
          <button
            type="button"
            onClick={handleCalculate}
            disabled={calculating || !isBjoechia}
            title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
            className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
          >
            {calculating ? tt('Calculating...', '计算中...') : tt('Calculate', '计算')}
          </button>
          {activeTab === 'team' && user.role === 'superadmin' && (
            <button
              type="button"
              onClick={handleRecalculateAll}
              disabled={recalculatingAll}
              title={tt("Refresh every already-existing pending/flagged Team Booster record using everyone's current star rank (Calculate only picks up brand-new orders, never existing ones)", '使用每个人当前的星级重新刷新所有已存在的待发放/已标记团队激励记录（"计算"只会拾取全新订单，不会更新已有记录）')}
              className="px-4 py-2 rounded-lg font-medium border disabled:opacity-50"
              style={{ backgroundColor: 'transparent', color: '#c9a961', borderColor: '#c9a961' }}
            >
              {recalculatingAll ? tt('Recalculating...', '重新计算中...') : tt('Recalculate', '重新计算')}
            </button>
          )}
        </div>
        )}
      </div>
      {!isBjoechia && (
        <p className="text-body-xs mb-4" style={{ color: '#999999' }}>
          {tt('Only WELLIS-BJOECHIA can run Calculate or Release.', '只有 WELLIS-BJOECHIA 可以执行"计算"或"发放"操作。')}
        </p>
      )}

      <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
        <TabButton active={activeTab === 'company'} onClick={() => setActiveTab('company')}>{tt('Passive', '被动')}</TabButton>
        <TabButton active={activeTab === 'team'} onClick={() => setActiveTab('team')}>{tt('Team Booster', '团队激励')}</TabButton>
        <TabButton active={activeTab === 'rates'} onClick={() => setActiveTab('rates')}>{tt('Rates', '比例设置')}</TabButton>
      </div>

      {error && (
        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#2a0d0d', borderColor: '#c94961', color: '#c94961' }}>
          {error}
        </div>
      )}
      {statusMessage && (
        <div className="border rounded-lg p-4 mb-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961', color: '#b8a68f' }}>
          {statusMessage}
        </div>
      )}

      {activeTab === 'team' ? (
        <>
          <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            {run ? (
              <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                {tt('Bonus collecting', '奖金累积中')}: <span style={{ color: '#d97706' }}>{bonusCollecting.toFixed(2)} CP</span>
              </p>
            ) : (
              <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Not yet calculated for this month.', '本月尚未计算。')}</p>
            )}
          </div>

          <div className="flex gap-2 mb-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
            <TabButton active={teamStatusTab === 'pending'} onClick={() => setTeamStatusTab('pending')}>{tt('Pending', '待发放')} ({pendingRecords.length})</TabButton>
            <TabButton active={teamStatusTab === 'released'} onClick={() => setTeamStatusTab('released')}>{tt('Released', '已发放')} ({releasedRecords.length})</TabButton>
            <TabButton active={teamStatusTab === 'flagged'} onClick={() => setTeamStatusTab('flagged')}>{tt('Flagged', '已标记')} ({flaggedRecords.length})</TabButton>
            <TabButton active={teamStatusTab === 'exclusions'} onClick={() => setTeamStatusTab('exclusions')}>{tt('Exclusions', '排除列表')} ({teamExclusions.length})</TabButton>
          </div>

          {teamStatusTab === 'exclusions' ? (
            teamExclusionsLoading ? (
              <p style={{ color: '#b8a68f' }}>{tt('Loading exclusions...', '正在加载排除列表...')}</p>
            ) : teamExclusions.length === 0 ? (
              <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>{tt('No excluded purchases. Use the "Exclude" button on a pending or flagged record to stop it from being recalculated in the future.', '暂无已排除的购买记录。可在待发放或已标记的记录上点击"排除"按钮，使其不再被重新计算。')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                  <thead>
                    <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                      <th className="p-2">{tt('Downline', '下线')}</th>
                      <th className="p-2">{tt('Package', '配套')}</th>
                      <th className="p-2">{tt('Reason', '原因')}</th>
                      <th className="p-2">{tt('Excluded By', '操作人')}</th>
                      <th className="p-2">{tt('Date', '日期')}</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamExclusions.map((ex) => (
                      <tr key={ex.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                        <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(ex.buyer)}</td>
                        <td className="p-2">{ex.package_code} ({Number(ex.package_price).toFixed(2)} CP)</td>
                        <td className="p-2">{ex.reason || '—'}</td>
                        <td className="p-2">{memberLabel(ex.excluded_by)}</td>
                        <td className="p-2">{new Date(ex.created_at).toLocaleDateString()}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => handleRemoveTeamExclusion(ex.id)}
                            disabled={teamExcludingId === ex.id}
                            className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                          >
                            {teamExcludingId === ex.id ? tt('Removing...', '移除中...') : tt('Re-include', '重新纳入')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
          <>
          {teamStatusTab === 'pending' && pendingRecords.length > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <label className="flex items-center gap-2 text-body-sm" style={{ color: '#b8a68f' }}>
                <input type="checkbox" checked={allPendingSelected} onChange={toggleSelectAll} />
                {tt('Select all pending', '全选待发放')}
              </label>
              <button
                type="button"
                onClick={() => handleRelease(Object.keys(selectedIds).filter((id) => selectedIds[id]))}
                disabled={releasing || selectedCount === 0 || !isBjoechia}
                title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
              >
                {releasing ? tt('Releasing...', '发放中...') : tt(`Release Selected (${selectedCount})`, `发放所选 (${selectedCount})`)}
              </button>
            </div>
          )}

          {loading ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : records.length === 0 ? (
            <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt("No records yet. Click Calculate to compute this month's Team Booster bonuses.", '暂无记录。点击"计算"以计算本月的团队激励奖金。')}</p>
            </div>
          ) : teamTabRecords.length === 0 ? (
            <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt(`No ${teamStatusTab} records.`, `暂无${teamStatusTab === 'pending' ? '待发放' : teamStatusTab === 'released' ? '已发放' : '已标记'}记录。`)}</p>
            </div>
          ) : (
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2"></th>
                    <th className="p-2">{tt('Recipient', '接收人')}</th>
                    <th className="p-2">{tt('Downline', '下线')}</th>
                    <th className="p-2">{tt('Downline Passive', '下线被动')}</th>
                    <th className="p-2">{tt('Star %', '星级 %')}</th>
                    <th className="p-2">{tt('Cash Points', '现金积分')}</th>
                    <th className="p-2">{tt('Product Points', '产品积分')}</th>
                    <th className="p-2">{tt('Status', '状态')}</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {teamTabRecords.map((r) => (
                    <tr key={r.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                      <td className="p-2">
                        {r.status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={!!selectedIds[r.id]}
                            onChange={() => toggleSelect(r.id)}
                          />
                        )}
                      </td>
                      <td className="p-2" style={{ color: '#c9a961' }}>
                        {'★'.repeat(r.recipient_ranking_snapshot)} {memberLabel(r.recipient)}
                      </td>
                      <td className="p-2">{memberLabel(r.buyer)} ({r.buyer?.ranking ?? 0})</td>
                      <td className="p-2" title={tt("This downline's own current pending Passive amount", '该下线自身当前待发放的被动金额')}>{Number(r.buyer_pending_company_pool || 0).toFixed(2)}</td>
                      <td className="p-2">{r.star_percent_snapshot}%{r.same_rank_override && tt(' (10% override)', '（10% 封顶）')}</td>
                      <td className="p-2">
                        {r.status === 'released'
                          ? Number(r.cash_amount).toFixed(2)
                          : (Number(r.amount) * (splitCashPercent / 100)).toFixed(2)}
                      </td>
                      <td className="p-2">
                        {r.status === 'released'
                          ? Number(r.credit_amount).toFixed(2)
                          : (Number(r.amount) * (1 - splitCashPercent / 100)).toFixed(2)}
                      </td>
                      <td className="p-2"><StatusBadge status={r.status} isZh={isZh} /></td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          {r.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => handleRelease([r.id])}
                              disabled={releasing || !isBjoechia}
                              title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                              className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                              style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                            >
                              {tt('Release', '发放')}
                            </button>
                          )}
                          {(r.status === 'pending' || r.status === 'flagged') && (
                            <button
                              type="button"
                              onClick={() => handleExcludeTeam(r.id)}
                              disabled={teamExcludingId === r.id}
                              title={tt('Exclude this purchase from all future Team Booster calculations', '将此笔购买从未来所有团队激励计算中排除')}
                              className="px-3 py-1 rounded font-medium text-body-xs border disabled:opacity-50"
                              style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                            >
                              {teamExcludingId === r.id ? tt('Excluding...', '排除中...') : tt('Exclude', '排除')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}
        </>
      ) : activeTab === 'company' ? (
        <>
          {companyRun && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p className="text-body-xs mb-1" style={{ color: '#999999' }}>{tt('Total New Sales this Period', '本期新增销售总额')}</p>
                <p className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>{Number(companyRun.total_sales).toFixed(2)} CP</p>
              </div>
              <div className="border rounded-lg p-4" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p className="text-body-xs mb-1" style={{ color: '#999999' }}>{tt('Total Cash Points Used', '已使用现金积分总额')} <span style={{ color: '#999999' }}>{tt('(reference only, not included in calculation)', '（仅供参考，不计入计算）')}</span></p>
                <p className="text-headline-sm font-bold" style={{ color: '#c9a961' }}>{Number(companyRun.repurchase_pool_total).toFixed(2)} CPR</p>
              </div>
            </div>
          )}

          <div className="border rounded-lg p-4 mb-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            {companyRun ? (
              <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                {tt('Pool', '资金池')}: <span style={{ color: '#c9a961' }}>{Number(companyRun.pool_total).toFixed(2)} CP</span>
                {' · '}{tt('Total shares (cumulative)', '累计份额总数')}: <span style={{ color: '#c9a961' }}>{Number(companyRun.total_allocations).toFixed(2)}</span>
                {' · '}{tt('Rate', '比例')}: <span style={{ color: '#c9a961' }}>{companyRun.rate_percent_snapshot}%</span>
                {' · '}{tt(`${companyRun.orders_included} share${companyRun.orders_included === 1 ? '' : 's'} paid this period`, `本期已支付 ${companyRun.orders_included} 份`)}
              </p>
            ) : (
              <p className="text-body-sm" style={{ color: '#b8a68f' }}>{tt('Not yet calculated for this month.', '本月尚未计算。')}</p>
            )}
          </div>

          <div className="flex gap-2 mb-4 border-b" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
            <TabButton active={companyStatusTab === 'pending'} onClick={() => setCompanyStatusTab('pending')}>{tt('Pending', '待发放')} ({companyPendingRecords.length})</TabButton>
            <TabButton active={companyStatusTab === 'released'} onClick={() => setCompanyStatusTab('released')}>{tt('Released', '已发放')} ({companyReleasedRecords.length})</TabButton>
            <TabButton active={companyStatusTab === 'flagged'} onClick={() => setCompanyStatusTab('flagged')}>{tt('Flagged', '已标记')} ({companyFlaggedRecords.length})</TabButton>
            <TabButton active={companyStatusTab === 'exclusions'} onClick={() => setCompanyStatusTab('exclusions')}>{tt('Exclusions', '排除列表')} ({companyExclusions.length})</TabButton>
          </div>

          {companyStatusTab === 'exclusions' ? (
            companyExclusionsLoading ? (
              <p style={{ color: '#b8a68f' }}>{tt('Loading exclusions...', '正在加载排除列表...')}</p>
            ) : companyExclusions.length === 0 ? (
              <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
                <p style={{ color: '#b8a68f' }}>{tt('No excluded purchases. Use the "Exclude" button on a pending or flagged record to stop it from being recalculated in the future.', '暂无已排除的购买记录。可在待发放或已标记的记录上点击"排除"按钮，使其不再被重新计算。')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto mb-8">
                <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                  <thead>
                    <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                      <th className="p-2">{tt('Buyer', '买家')}</th>
                      <th className="p-2">{tt('Package', '配套')}</th>
                      <th className="p-2">{tt('Reason', '原因')}</th>
                      <th className="p-2">{tt('Excluded By', '操作人')}</th>
                      <th className="p-2">{tt('Date', '日期')}</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {companyExclusions.map((ex) => (
                      <tr key={ex.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                        <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(ex.buyer)}</td>
                        <td className="p-2">
                          {ex.package_code} ({Number(ex.package_price).toFixed(2)} CP)
                          {ex.source === 'pre_processed' && (
                            <span className="ml-2 px-2 py-0.5 rounded text-body-xs font-medium" style={{ backgroundColor: 'rgba(201,169,97,0.15)', color: '#c9a961' }}>
                              {tt('Pre-processed', '预处理')}
                            </span>
                          )}
                        </td>
                        <td className="p-2">{ex.reason || '—'}</td>
                        <td className="p-2">{memberLabel(ex.excluded_by)}</td>
                        <td className="p-2">{new Date(ex.created_at).toLocaleDateString()}</td>
                        <td className="p-2">
                          <button
                            type="button"
                            onClick={() => handleRemoveCompanyExclusion(ex.id)}
                            disabled={companyExcludingId === ex.id}
                            className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                            style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                          >
                            {companyExcludingId === ex.id ? tt('Removing...', '移除中...') : tt('Re-include', '重新纳入')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (
          <>
          {companyStatusTab === 'pending' && companyPendingRecords.length > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <label className="flex items-center gap-2 text-body-sm" style={{ color: '#b8a68f' }}>
                <input type="checkbox" checked={companyAllPendingSelected} onChange={toggleCompanySelectAll} />
                {tt('Select all pending', '全选待发放')}
              </label>
              <button
                type="button"
                onClick={() => handleRelease(Object.keys(companySelectedIds).filter((id) => companySelectedIds[id]))}
                disabled={releasing || companySelectedCount === 0 || !isBjoechia}
                title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                className="px-4 py-2 rounded-lg font-medium disabled:opacity-50"
                style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
              >
                {releasing ? tt('Releasing...', '发放中...') : tt(`Release Selected (${companySelectedCount})`, `发放所选 (${companySelectedCount})`)}
              </button>
            </div>
          )}

          {companyLoading ? (
            <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
          ) : companyRecords.length === 0 ? (
            <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt("No records yet. Click Calculate to compute this month's Passive distribution.", '暂无记录。点击"计算"以计算本月的被动分配。')}</p>
            </div>
          ) : companyTabRecords.length === 0 ? (
            <div className="border rounded-lg p-6 text-center mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <p style={{ color: '#b8a68f' }}>{tt(`No ${companyStatusTab} records.`, `暂无${companyStatusTab === 'pending' ? '待发放' : companyStatusTab === 'released' ? '已发放' : '已标记'}记录。`)}</p>
            </div>
          ) : (
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                <thead>
                  <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                    <th className="p-2"></th>
                    <th className="p-2">{tt('Recipient', '接收人')}</th>
                    <th className="p-2">{tt('Package', '配套')}</th>
                    <th className="p-2">{tt('Weight', '权重')}</th>
                    <th className="p-2">{tt('Cash Points', '现金积分')}</th>
                    <th className="p-2">{tt('Product Points', '产品积分')}</th>
                    <th className="p-2">{tt('Status', '状态')}</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {companyTabRecords.map((r) => (
                    <tr key={r.id} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                      <td className="p-2">
                        {r.status === 'pending' && (
                          <input
                            type="checkbox"
                            checked={!!companySelectedIds[r.id]}
                            onChange={() => toggleCompanySelect(r.id)}
                          />
                        )}
                      </td>
                      <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(r.recipient)}</td>
                      <td className="p-2">{r.package_code} ({Number(r.package_price).toFixed(2)} CP){r.source === 'pre_processed' && (
                        <span className="ml-2 px-2 py-0.5 rounded text-body-xs font-medium" style={{ backgroundColor: 'rgba(201,169,97,0.15)', color: '#c9a961' }}>{tt('Pre-processed', '预处理')}</span>
                      )}</td>
                      <td className="p-2">{r.allocation_weight_snapshot}</td>
                      <td className="p-2">
                        {r.status === 'released'
                          ? Number(r.cash_amount).toFixed(2)
                          : (Number(r.amount) * (companySplitPercent / 100)).toFixed(2)}
                      </td>
                      <td className="p-2">
                        {r.status === 'released'
                          ? Number(r.credit_amount).toFixed(2)
                          : (Number(r.amount) * (1 - companySplitPercent / 100)).toFixed(2)}
                      </td>
                      <td className="p-2"><StatusBadge status={r.status} isZh={isZh} /></td>
                      <td className="p-2">
                        <div className="flex gap-2">
                          {r.status === 'pending' && (
                            <button
                              type="button"
                              onClick={() => handleRelease([r.id])}
                              disabled={releasing || !isBjoechia}
                              title={!isBjoechia ? tt('Only WELLIS-BJOECHIA can run this', '只有 WELLIS-BJOECHIA 可以执行此操作') : undefined}
                              className="px-3 py-1 rounded font-medium text-body-xs disabled:opacity-50"
                              style={{ backgroundColor: '#c9a961', color: '#0d2818' }}
                            >
                              {tt('Release', '发放')}
                            </button>
                          )}
                          {(r.status === 'pending' || r.status === 'flagged') && (
                            <button
                              type="button"
                              onClick={() => handleExcludeCompany(r.id)}
                              disabled={companyExcludingId === r.id}
                              title={tt('Exclude this purchase from all future Passive calculations', '将此笔购买从未来所有被动计算中排除')}
                              className="px-3 py-1 rounded font-medium text-body-xs border disabled:opacity-50"
                              style={{ backgroundColor: 'transparent', color: '#c94961', borderColor: '#c94961' }}
                            >
                              {companyExcludingId === r.id ? tt('Excluding...', '排除中...') : tt('Exclude', '排除')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>
          )}
        </>
      ) : (
        <>
          <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Team Booster Distribution', '团队激励分配')}</h3>
            <div className="space-y-3 mb-6">
              <NumberEditor
                label={tt('Passive Rate', '被动比例')}
                value={passiveMonthlyRate ? Number(passiveMonthlyRate.rate_percent) : null}
                onSave={(v) => handleSavePayoutRate('PASSIVE_MONTHLY', v)}
                saving={savingRateKey === 'PASSIVE_MONTHLY'}
                isZh={isZh}
              />
              <NumberEditor
                label={tt('Cash Points Split', '现金积分分配比例')}
                value={splitCashRate ? Number(splitCashRate.rate_percent) : null}
                onSave={(v) => handleSavePayoutRate('PASSIVE_MONTHLY_SPLIT_CASH_PERCENT', v)}
                saving={savingRateKey === 'PASSIVE_MONTHLY_SPLIT_CASH_PERCENT'}
                isZh={isZh}
              />
              <p className="text-body-xs pl-3" style={{ color: '#999999' }}>
                {tt(`Product Points Split: ${(100 - splitCashPercent).toFixed(2)}% (derived)`, `产品积分分配比例：${(100 - splitCashPercent).toFixed(2)}%（自动推导）`)}
              </p>
            </div>

            <h4 className="text-body-sm font-bold mb-3" style={{ color: '#c9a961' }}>{tt('Team Booster Rates', '团队激励比例')}</h4>
            <div className="space-y-3">
              {starRates.map((sr) => (
                <NumberEditor
                  key={sr.star}
                  label={tt(`${'★'.repeat(sr.star)}${'☆'.repeat(7 - sr.star)} (${sr.star} star)`, `${'★'.repeat(sr.star)}${'☆'.repeat(7 - sr.star)}（${sr.star} 星）`)}
                  value={Number(sr.percent)}
                  onSave={(v) => handleSaveStarRate(sr.star, v)}
                  saving={savingRateKey === `star-${sr.star}`}
                  isZh={isZh}
                />
              ))}
            </div>
          </div>

          <div className="border rounded-lg p-6 mt-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Passive Rates', '被动比例')}</h3>
            <div className="space-y-3 mb-6">
              <NumberEditor
                label={tt('Passive Distribution Rate', '被动分配比例')}
                value={companyRate ? Number(companyRate.rate_percent) : null}
                onSave={(v) => handleSaveCompanyRate('COMPANY_POOL_RATE', v)}
                saving={savingPackageKey === 'COMPANY_POOL_RATE'}
                isZh={isZh}
              />
              <NumberEditor
                label={tt('Cash Points Split', '现金积分分配比例')}
                value={companySplitRate ? Number(companySplitRate.rate_percent) : null}
                onSave={(v) => handleSaveCompanyRate('COMPANY_POOL_SPLIT_CASH_PERCENT', v)}
                saving={savingPackageKey === 'COMPANY_POOL_SPLIT_CASH_PERCENT'}
                isZh={isZh}
              />
              <p className="text-body-xs pl-3" style={{ color: '#999999' }}>
                {tt(`Product Points Split: ${(100 - companySplitPercent).toFixed(2)}% (derived)`, `产品积分分配比例：${(100 - companySplitPercent).toFixed(2)}%（自动推导）`)}
              </p>
            </div>

            <h4 className="text-body-sm font-bold mb-3" style={{ color: '#c9a961' }}>{tt('Package Allocation Weights & Passive Caps', '配套分配权重与被动收益上限')}</h4>
            <p className="text-body-xs mb-3" style={{ color: '#999999' }}>
              {tt(
                "Weight controls each package's share of the Passive distribution. A blank weight excludes the package entirely (e.g. L001). Max passive is a per-purchase lifetime cap on released Passive payout — once reached, further payouts tied to that purchase (across Passive, Direct Referral, and Team Booster) are flagged for manual review instead of auto-released. Leave blank for uncapped.",
                '权重控制每个配套在被动分配中的份额。权重留空则完全排除该配套（例如 L001）。最大被动收益是针对单笔购买、已发放被动的终身累计上限——一旦达到上限，之后针对该笔购买产生的新记录（涵盖被动、直推、团队激励）会自动标记为待人工复核，而非自动发放。留空表示不设上限。'
              )}
            </p>
            <div className="space-y-3">
              {companyPackages.map((pkg) => (
                <div key={pkg.code} className="border rounded p-3" style={{ borderColor: 'rgba(201,169,97,0.3)' }}>
                  <p className="text-body-sm font-medium mb-2" style={{ color: '#c9a961' }}>
                    {pkg.code} — {pkg.name_en} ({Number(pkg.price).toFixed(2)} CP)
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <NumberEditor
                      label={tt('Allocation Weight', '分配权重')}
                      value={pkg.allocation_weight === null ? null : Number(pkg.allocation_weight)}
                      onSave={(v) => handleSaveWeight(pkg.code, v)}
                      saving={savingPackageKey === `weight-${pkg.code}`}
                      min={0.01}
                      max={null}
                      allowClear
                      suffix={tt(' Shares', ' 股')}
                      isZh={isZh}
                    />
                    <NumberEditor
                      label={tt('Max Passive Payout (Cash Points)', '最大被动收益（现金积分）')}
                      value={pkg.max_passive_payout_cash === null || pkg.max_passive_payout_cash === undefined ? null : Number(pkg.max_passive_payout_cash)}
                      onSave={(v) => handleSaveMaxPassiveCash(pkg.code, v)}
                      saving={savingPackageKey === `max-cash-${pkg.code}`}
                      min={0.01}
                      max={null}
                      allowClear
                      suffix=" CP"
                      isZh={isZh}
                    />
                    <NumberEditor
                      label={tt('Max Passive Payout (Product Points)', '最大被动收益（产品积分）')}
                      value={pkg.max_passive_payout_credit === null || pkg.max_passive_payout_credit === undefined ? null : Number(pkg.max_passive_payout_credit)}
                      onSave={(v) => handleSaveMaxPassiveCredit(pkg.code, v)}
                      saving={savingPackageKey === `max-credit-${pkg.code}`}
                      min={0.01}
                      max={null}
                      allowClear
                      suffix=" CP"
                      isZh={isZh}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border rounded-lg p-6 mt-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>{tt('Cumulative Points by Owner', '按持有人累计积分')}</h3>
            {cumulativeByOwnerLoading ? (
              <p style={{ color: '#b8a68f' }}>{tt('Loading...', '加载中...')}</p>
            ) : cumulativeByOwner.length === 0 ? (
              <p style={{ color: '#b8a68f' }}>{tt('No released Passive payouts yet.', '暂无已发放的被动收益。')}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-body-sm" style={{ color: '#b8a68f' }}>
                  <thead>
                    <tr className="text-left border-b" style={{ borderColor: '#c9a961' }}>
                      <th className="p-2">{tt('Recipient', '接收人')}</th>
                      <th className="p-2">{tt('Package', '配套')}</th>
                      <th className="p-2">{tt('Cumulative Cash Points Received', '累计现金积分')}</th>
                      <th className="p-2">{tt('Cumulative Product Points Received', '累计产品积分')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cumulativeByOwner.map((o, idx) => (
                      <tr key={`${o.recipient?.id || idx}-${o.package_order_id || o.source_credit_transaction_id}`} className="border-b" style={{ borderColor: 'rgba(201,169,97,0.2)' }}>
                        <td className="p-2" style={{ color: '#c9a961' }}>{memberLabel(o.recipient)}</td>
                        <td className="p-2">{o.package_code} ({Number(o.package_price).toFixed(2)} CP)</td>
                        <td className="p-2">{Number(o.cash_received).toFixed(2)} CP</td>
                        <td className="p-2">{Number(o.credit_received).toFixed(2)} CP</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
    </div>
  );
}
